import { localeDict } from '@legacy/scripts/modules/locale/locale-dict';

import type { ApplyGradientToGridProps, GetGradientColorProps, RGBColorType } from '../types/utils/colors';

function getSmsStatusColor(status: string) {
    switch (status) {
        case 'Передача не удалась':
            return '#f00';
        case 'Успешно передано':
            return '#008000';
        case 'Ожидаем ответ':
        case 'Ожидает отправки':
            return '#00f';
    }
    return '#fff';
}

function getTicketStatusColor(status: number | string) {
    switch (status) {
        case 1:
        case 'Проверить выполнение':
            return '#ff0';
        case 5:
        case 'Проверено':
            return '#ff862e';
        case 2:
        case 'Доработать':
            return '#ffe256';
        case 3:
        case 'Успешно завершено':
        case 4:
        case 'Закрыто за ненадобностью':
            return '#dbdbdb';
    }
    return '#f9fbff';
}

function getTicketPriorityColor(priority: string) {
    switch (priority) {
        case 'В свободное время':
            return '#dbdbdb';
        case 'Важный':
            return '#ff0';
        case 'Критично важный':
            return '#f0aaaa';
        case 'Цель месяца':
            return '#c7ffff';
        case 'На паузе':
            return '#42f471';
    }
    return '#f9fbff';
}

function getTicketPenaltyColor(penalty: string) {
    return parseInt(penalty) === 0 ? '#f9fbff' : '#f0aaaa';
}

function getTicketDeadlineColor(dtEnd: string | Date) {
    const parsedDate = typeof dtEnd === 'string' ? window.parseDateTime(dtEnd) : dtEnd;
    if (parsedDate === null) {
        return '#ffc0cb';
    }
    return parsedDate < new Date() ? '#ffc0cb' : '#f9fbff';
}

function getTimeSpanColor(data: unknown, invert: boolean = false) {
    if (typeof data === 'string') {
        switch (invert ? !data.includes('-') : data.includes('-')) {
            case true:
                return '#ffc0cb';
            case false:
                return '#90ee90';
        }
    }
    return '#f9fbff';
}

function invertColor(hex: string, isContrastingColor: boolean): string {
    if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    if (hex.length !== 6) {
        throw new Error('Invalid HEX color.');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (isContrastingColor) {
        // http://stackoverflow.com/a/3943023/112731
        // Снижаем порог контрастности со стандартных 186 до 75, чтобы белый текст был только на черном фоне.
        const CONTRAST_THRESHOLD = 75;
        return (r * 0.299 + g * 0.587 + b * 0.114) > CONTRAST_THRESHOLD ? '#000' : '#fff';
    }

    // invert color components
    const redStr = (255 - r).toString(16);
    const greenStr = (255 - g).toString(16);
    const blueStr = (255 - b).toString(16);

    // pad each with zeros and return
    return '#' + window.padZero(redStr) + window.padZero(greenStr) + window.padZero(blueStr);
}

/**
 * Функция для градиентной покраски ячеек в гриде в соответствии с их значением.
 * */
function getGradientColor({ value, min, max, startColor, middleColor, endColor }: GetGradientColorProps) {
    // Нормализуем значение в диапазоне от 0 до 1
    const normalizedValue = (value - min) / (max - min);
    // Усиления контраста цвета
    const enhancedFactor = Math.pow(normalizedValue, 2.4);
    // Интерполяция цвета
    const interpolate = (start: number, end: number, factor: number) => Math.round(start + (end - start) * factor);

    if (enhancedFactor <= 0.5) {
        const subFactor = enhancedFactor * 2;
        const color = startColor.map((start, index) => interpolate(start, middleColor[index], subFactor));
        return `rgb(${color.join(', ')})`;
    }
    else {
        const subFactor = (enhancedFactor - 0.5) * 2;
        const color = middleColor.map((middle, index) => interpolate(middle, endColor[index], subFactor));
        return `rgb(${color.join(', ')})`;
    }
}

function applyGradientToGrid({ instanceGrid }: ApplyGradientToGridProps) {
    if (!instanceGrid) {
        console.error('Grid not found.');
        return;
    }

    const columns = instanceGrid.columns.filter((c) => c.attributes?.['data-gradient']).map(({ attributes, field }) => {
        const [start, middle, end] = [
            attributes?.['data-start-color'],
            attributes?.['data-middle-color'],
            attributes?.['data-end-color']
        ];

        if (!attributes?.['data-gradient'] || !start || !middle || !end) {
            console.error(`Could not find gradient colors attribute for column: ${field}.`);
            return {};
        }

        return {
            field,
            startColor: start.split(/,\s+/).map(Number) as RGBColorType,
            middleColor: middle.split(/,\s+/).map(Number) as RGBColorType,
            endColor: end.split(/,\s+/).map(Number) as RGBColorType
        };
    });
    const data = instanceGrid.dataSource.data();

    columns.forEach(({ field, startColor, middleColor, endColor }) => {
        if (!field) {
            console.error(`Column with name ${field} does not exist.`);
            return;
        }

        const values = data.map((item) => item[field as never]);
        const [min, max] = [Math.min(...values), Math.max(...values)];
        const { columnIndex, scope } = window.getColumnIndexWithScopeByField(instanceGrid, field);

        scope.find('.k-master-row').each((_, rowElement) => {
            const row = $(rowElement);
            const dataItem = instanceGrid.dataItem(row);
            const value: unknown = dataItem?.[field as never];

            if (typeof value === 'number') {
                const color = getGradientColor({ value, min, max, startColor, middleColor, endColor });
                const cell = row.find(`td:eq(${columnIndex})`);
                cell.css('background-color', color);
            }
        });
    });
}

/**
 * Применяет стили фона и цвета текста к элементу ComboBox.
 * */
function applyComboBoxColorStyles(comboBox: kendo.ui.ComboBox): void {
    const dataItem = comboBox.dataItem();
    const ALL_VALUE = -1;

    if (!dataItem || dataItem.id === ALL_VALUE) {
        return;
    }

    const container = comboBox.wrapper;

    if ('Value' in dataItem && dataItem.Value) {
        container.css('background', dataItem.Value);
    }

    if ('TextColor' in dataItem && dataItem.TextColor) {
        container.css('color', dataItem.TextColor);
    }
}

function getColorCellsWithValueYesNoUnknown(value: string) {
    const localeDictValues = Object.values(localeDict);

    const colorRules = [
        {
            values: localeDictValues.map((item) => item.mvcTemplateText.yes),
            color: 'rgb(144, 238, 144)'
        },
        {
            values: localeDictValues.map((item) => item.mvcTemplateText.no),
            color: 'rgb(255, 192, 203)'
        },
        {
            values: ['Неизвестно', 'Не требуется', 'Unknown', 'Not required'],
            color: 'rgb(255, 255, 0)'
        }
    ];

    return colorRules.find((rule) => rule.values.includes(value.trim()))?.color ?? '';
}

/**
 * Возвращает цвет для ячейки на основе значения с настраиваемыми порогами
 * @param {number} value - значение для анализа
 * @param {number} threshold - пороговое значение (по умолчанию 100)
 * @param {string} colorIfLess - цвет, если value < threshold (по умолчанию красный)
 * @param {string} colorIfEqual - цвет, если value == threshold (по умолчанию зеленый)
 * @param {string} colorIfGreater - цвет, если value > threshold (по умолчанию без цвета)
 * @returns {string} цвет в формате rgb или hex
 */
function getValueColor(
    value: number,
    threshold: number = 100,
    colorIfLess: string = '#FFC0CB',
    colorIfEqual: string = '#90EE90',
    colorIfGreater: string = ''
): string {
    if (value < threshold) {
        return colorIfLess;
    }
    if (value === threshold) {
        return colorIfEqual;
    }
    if (value > threshold) {
        return colorIfGreater;
    }
    return '';
}

window.getTimeSpanColor = getTimeSpanColor;
window.getSmsStatusColor = getSmsStatusColor;
window.getTicketStatusColor = getTicketStatusColor;
window.getTicketDeadlineColor = getTicketDeadlineColor;
window.getTicketPenaltyColor = getTicketPenaltyColor;
window.invertColor = invertColor;
window.applyComboBoxColorStyles = applyComboBoxColorStyles;
window.getTicketPriorityColor = getTicketPriorityColor;
window.applyGradientToGrid = applyGradientToGrid;
window.getColorCellsWithValueYesNoUnknown = getColorCellsWithValueYesNoUnknown;
window.getValueColor = getValueColor;
